import { useMemo } from 'react'
import { handle } from 'common/utils'
import routes from 'common/routes'
import { capitalize } from 'lodash'
import { useCurrentUserId } from 'universal/hooks'
import { humanizeVote, addressFromPageKey } from 'common/logic'
import { normalizeReview } from 'common/utils'

const contextualName = ({ uid, author, possessive }) => {
  if (uid && author.id === uid) {
    return 'my'
  } else {
    const base = handle(author)
    return possessive ? `${base}'s` : base
  }
}
const useShareableReviewComponents = ({
  getComponents,
  review,
  domainKey,
  pageKey,
  author,
  alreadyParsed,
  ...props
}) => {
  const uid = useCurrentUserId()
  return useMemo(() => {
    if (alreadyParsed) return alreadyParsed

    return getComponents({
      review,
      domainKey,
      author,
      uid,
      pageKey,
    })
  }, [getComponents, review, domainKey, author, alreadyParsed, uid, pageKey])
}

export const useShareableDomainReviewComponents = (props) =>
  useShareableReviewComponents({
    ...props,
    getComponents: getDomainReviewComponents,
  })

export const useShareablePageReviewComponents = (props) =>
  useShareableReviewComponents({
    ...props,
    getComponents: getPageReviewComponents,
  })

const getDomainReviewComponents = ({ domainPacket, review, domainKey, author, uid }) => {
  const my = contextualName({ uid, author, possessive: true })

  // Note: if given explicit review to share, won't have meta.numPageReviews... that's probably fine, minor tweaks to shared messages for now.
  // May be worth breaking up the isomorphic <ShareReview /> approach and just pass domainPacket for domain reviews?
  const { comment, vote, hashtags, meta } = normalizeReview(review || domainPacket?.domainReview)

  const humanVote = `${humanizeVote(vote)}${
    meta?.numPageReviews || meta?.numRuleReviews ? ' (with complications)' : ''
  }`

  return {
    url: routes.userDomainReviewUrl(author, domainKey),
    title: `${handle(author)}'s take on ${domainKey}`,
    displayHashtag: hashtags.length ? `#${hashtags[0]}` : null,
    fbQuote: comment, // TODO: see how it renders, but probably add some callback to the site
    summary: `In short, generally ${humanVote}... but check out ${my} full review of ${domainKey} for details.`,
    hashtags,

    // Email being sent directly to another user should use "my vote" voice. Others are public postings that should probably use "Kali's vote" voice.
    emailSubject: `${capitalize(my)} take on ${domainKey}`,
    emailBody: `In short, generally ${humanVote}. Check out the full review here:`,
  }
}

const getPageReviewComponents = ({ review, domainKey, pageKey, author, uid }) => {
  const my = contextualName({ uid, author, possessive: true })
  const { comment, vote, hashtags } = normalizeReview(review)
  const address = addressFromPageKey({
    pageKey,
    domainKey,
    review,
    withoutProtocol: true,
  })

  const humanVote = humanizeVote(vote)

  return {
    url: routes.userDomainPageReviewUrl(author, domainKey, pageKey),
    title: `${handle(author)}'s take on ${address}`,
    displayHashtag: hashtags.length ? `#${hashtags[0]}` : null,
    fbQuote: comment, // TODO: see how it renders, but probably add some callback to the site
    summary: `In short, generally ${humanVote}... but check out ${my} full review of ${address} for details.`,
    hashtags,

    // Email being sent directly to another user should use "my vote" voice. Others are public postings that should probably use "Kali's vote" voice.
    emailSubject: `${capitalize(my)} take on ${address}`,
    emailBody: `In short, generally ${humanVote}. Check out the full review here:`,
  }
}
